/* import __COLOCATED_TEMPLATE__ from './media-block.hbs'; */
/**

## Media Block

A Media Block is a section of content, usually with 2 parts: Some block of text content
which could include paragraphs, headlines, and even buttons, and a piece of media, which
could be an image, video, or animation.

Media blocks are designed to be used in combination with [Responsive Columns](./glass/docs/core-styling/responsive-columns.html) to create
versatile page layouts that can be used for upsells, onboarding, or other marketing purposes.

### Contextual Components

| Description | Contextual Component |
| --- | --- |
| Content | [media-block/content](./media-block/content.html) |
| Content>Healine{Primary,Secondary} | [media-block/headline](./media-block/headline.html) |
| Content>Button{Primary,Secondary} | [sq-button](./sq-button.html) |
| Content>Area | [media-block/section](./media-block/section.html) |
| Media | [media-block/media](./media-block/media.html) |
| Media>Image | [responsive-image](./responsive-image.html) |
| Media>Video | [video-player](./video-player.html) |

```example
@title Default
@description
By Default, the media block is a full-width component that stacks vertically. Media and Content
will appear in the order you place them within the `{{media-block}}` container in your template.
Both the Content and media sections are optional.

@hbs
{{#media-block as |block|}}
  {{#block.content as |content|}}
    {{content.headline-primary text="Media Block Headline"}}
    {{#content.area}}
      <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
    {{/content.area}}
  {{/block.content}}

  {{#block.media as |media|}}
    {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                            2x="/assets/images/square-hardware-pos-stand@2x.png")}}
  {{/block.media}}
{{/media-block}}
```
```example
@title Headlines
@description
Media Blocks support a primary and secondary headline out of the box, but you can pass additional
headlines and even customize the heading tag if needed.

@hbs
{{#media-block as |block|}}
  {{#block.content as |content|}}
    {{content.headline-secondary text="Headline Secondary"}}
    {{content.headline-primary text="Headline Primary"}}
    {{content.headline tagName="h5" text="Custom Headline"}}
  {{/block.content}}
{{/media-block}}
```

```example
@title CTA Buttons
@description
Media Blocks support primary and secondary CTAs. The content section supports passing any
number of CTAs of any kind, but layout is optimized for maximum of 2 CTAs at one time.

@hbs
{{#media-block as |block|}}
  {{#block.content as |content|}}
    {{content.button-primary label="Primary CTA"}}
    {{content.button-secondary label="Secondary CTA"}}
  {{/block.content}}
{{/media-block}}
```

```example
@title Custom Content Blocks
@description
You can pass custom blocks to content areas using `content.area`

@hbs
{{#media-block as |block|}}
  {{#block.content as |content|}}
    {{#content.area}}
      <div class="product-logo">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.6576 0H3.34242C1.49668 0 0 1.49623 0 3.34242V16.6576C0 18.5038 1.49668 20 3.34242 20H16.6576C18.5038 20 20 18.5038 20 16.6576V3.34242C20.0005 1.49623 18.5042 0 16.6576 0ZM16.364 15.31C16.364 15.8926 15.8917 16.3644 15.3095 16.3644H4.69275C4.11008 16.3644 3.63785 15.8926 3.63785 15.31V4.69275C3.63785 4.11008 4.11008 3.63785 4.69275 3.63785H15.3095C15.8917 3.63785 16.364 4.11008 16.364 4.69275V15.31ZM7.87747 12.727H12.1216C12.457 12.727 12.727 12.4543 12.727 12.1184V7.88065C12.727 7.54477 12.457 7.27207 12.1216 7.27207H7.87747C7.54295 7.27207 7.27207 7.54477 7.27207 7.88065V12.1184C7.27207 12.4543 7.54295 12.727 7.87747 12.727Z" fill="black"/>
        </svg>
        <span class="logotype">Product Name</span>
      </div>
    {{/content.area}}
    {{#content.area}}
      <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
    {{/content.area}}
    {{content.button-primary label="Primary CTA"}}
  {{/block.content}}
{{/media-block}}
```

```example
@title Media Cropping
@description
Media will resize to fit the media space provided to it by default, but you can specify that media
instead attempt to fill the space provided and crop any excess accordingly by passing `crop="fill"`
to `{{block.media}}`.

@hbs
{{#media-block as |block|}}
  {{#block.content as |content|}}
    {{content.headline-primary text="Media Block Headline"}}
    {{#content.area}}
      <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
    {{/content.area}}
  {{/block.content}}

  {{#block.media crop="fill" as |media|}}
    {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                            2x="/assets/images/square-hardware-pos-stand@2x.png")}}
  {{/block.media}}
{{/media-block}}
```

```example
@title Horizontal Layout
@description
By default, media blocks have a linear vertical layout. Pass `mediaLayout="horizontal"` to have
your block display horizontally. (*NOTE*: This only works for media blocks that within a full-width
column span `.responsive-columns` container or not within a `.responsive-columns` container)

@hbs
{{#media-block mediaLayout="horizontal" as |block|}}
  {{#block.content as |content|}}
    {{content.headline-primary text="Media Block Headline"}}
    {{#content.area}}
      <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
    {{/content.area}}
  {{/block.content}}

  {{#block.media as |media|}}
    {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                            2x="/assets/images/square-hardware-pos-stand@2x.png")}}
  {{/block.media}}
{{/media-block}}
```

```example
@title Stacked Layout
@description
By default, media blocks have a linear vertical layout. Pass `mediaLayout="stacked"` and **make
sure to place your content section *inside* the media section** to have your content display on
top of your media.

@hbs
{{#media-block mediaLayout="stacked" as |block|}}
  {{#block.media as |media|}}

    {{! media (e.g. image/video) comes first}}
    {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                            2x="/assets/images/square-hardware-pos-stand@2x.png")}}

    {{! content comes second and uses media.content instead of block.content}}
    {{#media.content as |content|}}
      {{content.headline-primary text="Media Block Headline"}}
      {{#content.area}}
        <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
      {{/content.area}}
    {{/media.content}}

  {{/block.media}}
{{/media-block}}
```

```example
@title Video
@description
You can use a video instead of an image for the media

@hbs
{{#media-block as |block|}}
  {{#block.content as |content|}}
    {{content.headline-primary text="Media Block Headline"}}
    {{#content.area}}
      <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
    {{/content.area}}
  {{/block.content}}

  {{#block.media as |media|}}
    {{media.video src="/assets/vidoes/video-example.mp4"
                  poster="/assets/images/video-example-poster.png"}}
  {{/block.media}}
{{/media-block}}
```

```example
@title Background Video
@description
To make a background video, use mediaLayout=stacked on the media-block with crop=fill on block.media.
Stacked layout media blocks with video as their media will have `canInteract=false` passed automatically
which will hide the video controls UI as well as autoplay, playsinline, and loop.

@hbs

{{#media-block as |block|}}
  {{#block.content as |content|}}
    {{content.headline-primary text="Media Block Headline"}}
    {{#content.area}}
      <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
    {{/content.area}}
  {{/block.content}}

  {{#block.media crop="fill" as |media|}}
    {{media.video src="/assets/vidoes/video-background.mp4"
                  poster="/assets/images/video-background-poster.png"}}
  {{/block.media}}
{{/media-block}}
```

```example
@title Content Themes
@description
Change the content theme to `light` by passing `theme="light"` to {{block.content}}
An example of when you may want to do this is when you're using a stacked layout, and the default text color isn't providing
the right contrast for readability.

@hbs
{{#media-block mediaLayout="stacked" as |block|}}
  {{#block.media as |media|}}

    {{! media (e.g. image/video) comes first}}
    {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                            2x="/assets/images/square-hardware-pos-stand@2x.png")}}

    {{! content comes second and uses media.content instead of block.content}}
    {{#media.content theme="light" as |content|}}
      {{content.headline-primary text="Media Block Headline"}}
      {{#content.area}}
        <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
      {{/content.area}}
    {{/media.content}}

  {{/block.media}}
{{/media-block}}
```

```example
@title Media Overlays
@description
Additionally, with stacked layouts, the media might have too much contrast within itself for
proper ledgability of text on top of it. You can choose to place an overlay on the media by
passing a hash of options to an `overlay` param. You can choose a 'light' or 'dark' color and
choose whether or not the overlay should be a gradient and if so, what direction the gradient
should be coming from. See [media-block/media](./media-block/media.html) for more details.

@hbs
{{#media-block mediaLayout="stacked" as |block|}}
  {{! media (e.g. image/video) comes first}}
  {{#block.media overlay=(hash color="dark"
                               asGradient="true"
                               direction="bottom")
                 as |media|}}

    {{! media (e.g. image/video) comes first}}
    {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                            2x="/assets/images/square-hardware-pos-stand@2x.png")}}

    {{! content comes second and uses media.content instead of block.content}}
    {{#media.content theme="light" as |content|}}
      {{content.headline-primary text="Media Block Headline"}}
      {{#content.area}}
        <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
      {{/content.area}}
    {{/media.content}}

  {{/block.media}}
{{/media-block}}
```

```example
@title Use with Responsive Column Layouts
@description
When used in conjunction with the Responsive Columns[./glass/docs/core-styling/responsive-columns.html] containers, media blocks have additional
styling based on the number of colums within the responsive-column container.

@hbs
<h3>2-up or 6-column-span (half width media-blocks)</h3>
<div class="responsive-columns">
  {{#media-block class="responsive-column--half" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-secondary text="Secondary Headline"}}
      {{content.headline-primary text="Primary Headline"}}
      {{#content.area}}
        <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
      {{/content.area}}
      {{content.button-primary label="Primary CTA"}}
      {{content.button-secondary label="Secondary CTA"}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
  {{#media-block class="responsive-column--half" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-secondary text="Secondary Headline"}}
      {{content.headline-primary text="Primary Headline"}}
      {{#content.area}}
        <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
      {{/content.area}}
      {{content.button-primary label="Primary CTA"}}
      {{content.button-secondary label="Secondary CTA"}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
</div>

<h3>3-up or 4-column-span (third-width media blocks)</h3>
<div class="responsive-columns">
  {{#media-block class="responsive-column--third" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-primary text="Primary Headline"}}
      {{#content.area}}
        <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
      {{/content.area}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
  {{#media-block class="responsive-column--third" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-primary text="Primary Headline"}}
      {{#content.area}}
        <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
      {{/content.area}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
  {{#media-block class="responsive-column--third" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-primary text="Primary Headline"}}
      {{#content.area}}
        <p>I am a simple paragraph in the content section, used as a content.area. You can pass more complex HTML to this section, if you need to. You can also pass multiple paragraphs</p>
      {{/content.area}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
</div>

<h3>4-up or 3-column-span (quarter-width media blocks)</h3>
<div class="responsive-columns">
  {{#media-block class="responsive-column--quarter" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-primary text="Primary Headline"}}
      {{content.button-primary label="Primary CTA"}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
  {{#media-block class="responsive-column--quarter" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-primary text="Primary Headline"}}
      {{content.button-primary label="Primary CTA"}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
  {{#media-block class="responsive-column--quarter" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-primary text="Primary Headline"}}
      {{content.button-primary label="Primary CTA"}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
  {{#media-block class="responsive-column--quarter" as |block|}}
    {{#block.content as |content|}}
      {{content.headline-primary text="Primary Headline"}}
      {{content.button-primary label="Primary CTA"}}
    {{/block.content}}

    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}
  {{/media-block}}
</div>

<h3>6-up or 2-column-span (Sixth-width media blocks)</h3>
<div class="responsive-columns">
  {{#media-block class="media-block__responsive-column--sixth" as |block|}}
    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}

     {{#block.content as |content|}}
      {{content.headline-primary text="Headline"}}
    {{/block.content}}
  {{/media-block}}
  {{#media-block class="media-block__responsive-column--sixth" as |block|}}
    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}

     {{#block.content as |content|}}
      {{content.headline-primary text="Headline"}}
    {{/block.content}}
  {{/media-block}}
  {{#media-block class="media-block__responsive-column--sixth" as |block|}}
    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}

     {{#block.content as |content|}}
      {{content.headline-primary text="Headline"}}
    {{/block.content}}
  {{/media-block}}
  {{#media-block class="media-block__responsive-column--sixth" as |block|}}
    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}

     {{#block.content as |content|}}
      {{content.headline-primary text="Headline"}}
    {{/block.content}}
  {{/media-block}}
  {{#media-block class="media-block__responsive-column--sixth" as |block|}}
    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}

     {{#block.content as |content|}}
      {{content.headline-primary text="Headline"}}
    {{/block.content}}
  {{/media-block}}
  {{#media-block class="media-block__responsive-column--sixth" as |block|}}
    {{#block.media as |media|}}
      {{media.image src=(hash 1x="/assets/images/square-hardware-pos-stand.png"
                              2x="/assets/images/square-hardware-pos-stand@2x.png")}}
    {{/block.media}}

     {{#block.content as |content|}}
      {{content.headline-primary text="Headline"}}
    {{/block.content}}
  {{/media-block}}
</div>
```

### Parameters
@param {string} [mediaLayout="normal"] 'normal', 'horizontal', or 'stacked' (Note: horizontal direction only applies to media blocks within a full 12-column span `.responsive-columns` container or outside of a `.responsive-columns` container)
**/

import Component from '@ember/component';
import { computed } from '@ember/object';
export const MEDIA_BLOCK_LAYOUT = {
  LINEAR: 'linear',
  HORIZONTAL: 'horizontal',
  STACKED: 'stacked'
};

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['media-block'],
  classNameBindings: ['layoutClass'],
  attributeBindings: ['data-test-media-block'],
  'data-test-media-block': true,
  mediaLayout: MEDIA_BLOCK_LAYOUT.LINEAR,
  layoutClass: computed('mediaLayout', function () {
    if (this.mediaLayout !== MEDIA_BLOCK_LAYOUT.LINEAR) {
      return `media-block--${this.mediaLayout}`;
    } else {
      return '';
    }
  })
});